import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { UseQueryResult } from "@tanstack/react-query";
import { HIDDEN_FILE_KINDS } from "@utils/constants";
import { downloadLearnerFileById } from "@utils/helpers";
import { nonNullable } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  ApiLearnerFileFileKindChoices,
  ApiReportEhrSyncChoices,
  ApiReportReportStatusChoices,
  ApiReportReportTypeChoices,
  ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices,
  ApiReportTreatmentPlanAiStgPredictionStatusChoices,
  ApiReportTreatmentPlanAiTargetPredictionStatusChoices,
  GetReportTreatmentPlanByReportIdQuery,
  LearnerFileType,
  ReportSectionFieldsType,
  ReportSectionsType,
  ReportTypeEnums,
  useGetReassessmentReportGoalsQuery,
  useGetReportByIdQuery,
  useGetReportTreatmentPlanByReportIdQuery,
  useTreatmentPlanTemplateQuery,
} from "@api/graphql/types-and-hooks";
import { useLDReportTemplateContext } from "@hooks/useLDReportTemplateContext";
import { useDismissGasPartialWarningOnLeave } from "@pages/ClinicalReportDetails/ReportPage/hooks/useNavigateToReview";
import { isGeneratingSection, isNarrativeMappingInProgress } from "@pages/ClinicalReportDetails/helper";
import { useAdminData } from "@providers/AdminDataProvider";
import { ReportProvider } from "@providers/ReportProvider";

type ReportActiveTab = "programming" | "narrative";

/**
 * Action published by the Review page so the Reassessment step-progress
 * header (which lives in a parent component) can render the "Complete"
 * button up top instead of relying on the fixed footer row. Set to null
 * when the Review page unmounts or no action is available.
 */
interface StepHeaderCompleteAction {
  onComplete: () => void | Promise<void>;
  disabled: boolean;
  disabledTooltip: string;
}

interface ClinicalReportDetailsDataI {
  reportType: ReportTypeEnums;
  reportSections: ReportSectionsType[];
  previousAssessmentReports: LearnerFileType[];
  assessmentFiles: LearnerFileType[];
  downloadReportFileById: (fileId: string) => void;
  isReportCompleted: boolean;
  initialAssessmentTreatmentPlanQuery: UseQueryResult<GetReportTreatmentPlanByReportIdQuery, unknown>;
  appendContentToField: (fieldId: string, content: string) => ReportSectionFieldsType | null;
  setFieldContent: (fieldId: string, content: string) => void;
  isLoading: boolean;
  isReviewAndSignPage: boolean;
  isReportPage: boolean;
  isDiagnosisEvaluation: boolean;
  isInitialAssessment: boolean;
  isReassessment: boolean;
  activeTab: ReportActiveTab;
  isProgrammingOrNarrativePage: boolean;
  isEHRScraperUser: boolean;
  isProgressSummaryGenerating: boolean;
  // The report's template has reportNarrativeMappingWorkflow on and its
  // CentralReach learning-tree mapping is IN_PROGRESS. False otherwise,
  // including once the mapping is COMPLETED or FAILED or if it never started.
  // Holds only the narrative page.
  isNarrativeMappingInProgress: boolean;
  isGasEnabled: boolean;
  isAddTargetsEnabled: boolean;
  stepHeaderCompleteAction: StepHeaderCompleteAction | null;
  setStepHeaderCompleteAction: (action: StepHeaderCompleteAction | null) => void;
}

const ClinicalReportDetailsContext = createContext<ClinicalReportDetailsDataI | undefined>(undefined);

export const useClinicalReportDetailsData = () => {
  const context = useContext(ClinicalReportDetailsContext);
  if (context === undefined) {
    throw new Error("ClinicalReportDetails must be wrapped with ClinicalReportDetailsProvider");
  }
  return context;
};

/** Use when component may render outside ClinicalReportDetailsProvider (e.g. ReportCompletionDialog on Assessment flow). */
export const useClinicalReportDetailsDataOptional = () => useContext(ClinicalReportDetailsContext);

interface ClinicalReportDetailsProviderProps extends PropsWithChildren {
  /** Explicit report id override for callers mounted outside the `:reportId`
   *  route param (e.g. EvaluationDetails, whose route param is `evaluationId`).
   *  Falls back to the route param when omitted. */
  reportId?: string;
}

export const ClinicalReportDetailsProvider: React.FC<ClinicalReportDetailsProviderProps> = ({
  children,
  reportId: reportIdProp,
}) => {
  const { reportId: reportIdParam } = useParams();
  const reportId = reportIdProp ?? reportIdParam;
  const { pathname } = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const flags = useFlags();
  const { isHiRasmusUser, isMotivityUser, isCentralReachUser, isHippHealthUser, isEnsoraUser } = useAdminData();

  const currentRoute = pathname.split("#")?.pop()?.split("/").pop();

  const reportQuery = useGetReportByIdQuery(
    {
      reportId: reportId || "<missing-assessment-id>",
      filter: {},
    },
    {
      enabled: !!reportId,
      queryKey: ["get-report-by-id", reportId],
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const report = reportQuery.data?.getReports?.edges[0]?.node;
  const isReassessment = report?.reportType === ApiReportReportTypeChoices.Reassessment;

  useLDReportTemplateContext(report?.reportTemplate);

  const isGasEnabled = report?.reportTemplate?.treatmentPlanTemplate?.isGasEnabled ?? false;
  const isAddTargetsEnabled = report?.reportTemplate?.treatmentPlanTemplate?.isAddTargetsEnabled ?? true;

  // Prefetch programming data for reassessment so Goal Progress tab loads instantly
  useGetReassessmentReportGoalsQuery(
    {
      filter: {
        reportId: report?.id || "",
      },
    },
    {
      queryKey: ["get-reassessment-goals", report?.id, [], [], { preset: null, startDate: null, endDate: null }],
      enabled: isReassessment && !!report?.id,
      notifyOnChangeProps: [],
    },
  );

  useTreatmentPlanTemplateQuery(
    { reportId: report?.id || "" },
    {
      enabled: isReassessment && !!report?.id,
      retry: false,
      notifyOnChangeProps: [],
    },
  );

  const shouldPollForAiStg = () => {
    if (!isReassessment) return false;

    const report = reportQuery.data?.getReports?.edges[0]?.node;
    const reportTreatmentPlan = report?.reportTreatmentPlan;
    const aiStgStatus = reportTreatmentPlan?.aiStgPredictionStatus;

    const isStgGenerating =
      aiStgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.Pending ||
      aiStgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPending;

    return isStgGenerating;
  };

  const shouldPollForAiProgressSummary = () => {
    if (!isReassessment) return false;

    const report = reportQuery.data?.getReports?.edges[0]?.node;
    const reportTreatmentPlan = report?.reportTreatmentPlan;
    const aiProgressSummaryStatus = reportTreatmentPlan?.aiGoalProgressSummaryPredictionStatus;

    const isProgressSummaryGenerating =
      aiProgressSummaryStatus === ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices.Pending ||
      aiProgressSummaryStatus ===
        ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices.GoalPredictionPending;

    return isProgressSummaryGenerating;
  };

  const shouldPollForNarrativeMapping = () => {
    if (!isReassessment) return false;

    return isNarrativeMappingInProgress(reportQuery.data?.getReports?.edges[0]?.node);
  };

  const shouldPollForAiTargets = () => {
    if (!isReassessment) return false;

    const report = reportQuery.data?.getReports?.edges[0]?.node;
    const reportTreatmentPlan = report?.reportTreatmentPlan;
    const aiTargetsStatus = reportTreatmentPlan?.aiTargetPredictionStatus;

    return (
      aiTargetsStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.Pending ||
      aiTargetsStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPending
    );
  };

  const shouldPollForRegeneration = () => {
    const reportSections = reportQuery.data?.getReports?.edges[0]?.node?.reportSections?.edges?.map(
      (sectionNode) => sectionNode?.node,
    ) as ReportSectionsType[];
    if (!reportSections || reportSections.length === 0) return false;
    return isGeneratingSection(reportSections);
  };

  const shouldPollForEhrSync = () => {
    const report = reportQuery.data?.getReports?.edges[0]?.node;
    const ehrSyncStatus = report?.ehrSync;

    // Poll if EHR sync is in progress or pending
    return ehrSyncStatus === ApiReportEhrSyncChoices.Pending || ehrSyncStatus === ApiReportEhrSyncChoices.Inprogress;
  };

  const shouldPollForAnyAi = () => {
    return (
      shouldPollForAiStg() ||
      shouldPollForAiProgressSummary() ||
      shouldPollForNarrativeMapping() ||
      shouldPollForRegeneration() ||
      shouldPollForEhrSync() ||
      (!isGasEnabled && shouldPollForAiTargets())
    );
  };

  const reportQueryWithPolling = useGetReportByIdQuery(
    {
      reportId: reportId || "<missing-assessment-id>",
      filter: {},
    },
    {
      enabled: !!reportId,
      queryKey: ["get-report-by-id", reportId],
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: () => {
        if (shouldPollForAnyAi()) {
          return config.REPORT_POLLING_INTERVAL || 5000;
        }
        return false;
      },
    },
  );

  const initialAssessmentTreatmentPlanQuery = useGetReportTreatmentPlanByReportIdQuery(
    {
      reportId: reportId || "<missing-assessment-id>",
      filter: {},
    },
    {
      enabled: !!reportId,
      queryKey: ["get-report-treatment-plan-by-report-id", reportId],
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: () => {
        if (shouldPollForAnyAi()) {
          return config.REPORT_POLLING_INTERVAL || 5000;
        }
        return false;
      },
    },
  );

  const reportFinal = reportQueryWithPolling.data?.getReports?.edges[0]?.node;

  const isReportCompleted =
    reportFinal?.reportStatus === ApiReportReportStatusChoices.Completed ||
    reportFinal?.reportStatus === ApiReportReportStatusChoices.Generating;

  const isReviewAndSignPage = currentRoute === "review-and-sign";
  const isReportPage = currentRoute === "report-page";
  const isProgrammingOrNarrativePage = currentRoute === "programming" || currentRoute === "narrative";

  const isEHRScraperUser =
    isHiRasmusUser ||
    isMotivityUser ||
    isEnsoraUser ||
    isCentralReachUser ||
    (isHippHealthUser && flags["hipphealth-sync"]);

  const [previousAssessmentReports, assessmentFiles] = useMemo(() => {
    const files =
      reportQueryWithPolling.data?.getReports?.edges[0]?.node?.reportFiles.edges
        ?.map((edge) => edge?.node)
        .filter(nonNullable)
        .filter((file) => !HIDDEN_FILE_KINDS.includes(file.fileKind)) || [];

    return files.reduce<[LearnerFileType[], LearnerFileType[]]>(
      (acc, file) => {
        if (!file) return acc;
        const [previousAssessments, otherFiles] = acc;
        if (file.fileKind === ApiLearnerFileFileKindChoices.PreviousAssessment) {
          previousAssessments.push(file as LearnerFileType);
        } else {
          otherFiles.push(file as LearnerFileType);
        }
        return acc;
      },
      [[], []],
    );
  }, [reportQueryWithPolling.data]);

  const downloadReportFileById = useCallback(
    async (fileId: string) => {
      const token = await getAccessTokenSilently();
      await downloadLearnerFileById({ fileId: fileId, token: token });
    },
    [getAccessTokenSilently],
  );

  const activeTab: ReportActiveTab = currentRoute === "narrative" ? "narrative" : "programming";
  const [sections, setSections] = useState<ReportSectionsType[]>([]);

  useEffect(() => {
    const rawSections =
      (reportFinal?.reportSections?.edges?.map((sectionNode) => sectionNode?.node) as ReportSectionsType[]) ?? [];

    // Sort by page location order, then by rankOrder within each page location
    const pageLocationOrder = ["report-part-1", "report-part-2", "recommendations", "conclusions"];

    const sortedSections = rawSections.sort((a, b) => {
      const aLocationIndex = pageLocationOrder.indexOf(a?.pageLocation ?? "");
      const bLocationIndex = pageLocationOrder.indexOf(b?.pageLocation ?? "");

      // If both have valid page locations, sort by location first
      if (aLocationIndex !== -1 && bLocationIndex !== -1) {
        if (aLocationIndex !== bLocationIndex) {
          return aLocationIndex - bLocationIndex;
        }
      }
      // If only one has a valid page location, prioritize it
      else if (aLocationIndex !== -1) {
        return -1;
      } else if (bLocationIndex !== -1) {
        return 1;
      }

      // Within the same page location (or both unknown), sort by rankOrder
      return (a?.rankOrder ?? 0) - (b?.rankOrder ?? 0);
    });

    setSections(sortedSections);
  }, [reportFinal]);

  const appendContentToField = useCallback(
    (fieldId: string, content: string) => {
      let changedField = null;
      const newSections = sections.map((section) => {
        return {
          ...section,
          sectionField: {
            ...section.sectionField,
            edges: section.sectionField.edges.map((edge) => {
              const ret = { ...edge };
              if (ret?.node && ret.node.id === fieldId) {
                ret.node.content = (ret.node?.content || "") + content;
                changedField = { ...ret.node };
                changedField.reportSection = section;
              }
              return ret;
            }),
          },
        };
      }) as ReportSectionsType[];

      setSections(newSections);

      return changedField as ReportSectionFieldsType | null;
    },
    [sections],
  );

  // Targeted replace of a single field's content. Used after autosave so
  // downstream consumers that memo on `sections` (e.g. the Reassessment
  // Report Progress chip) see a fresh array reference without paying for a
  // full reportQuery.refetch on every keystroke-debounced WYSIWYG save.
  const setFieldContent = useCallback((fieldId: string, content: string) => {
    setSections(
      (prev) =>
        prev.map((section) => ({
          ...section,
          sectionField: {
            ...section.sectionField,
            edges: section.sectionField.edges.map((edge) => {
              if (edge?.node?.id !== fieldId) return edge;
              return { ...edge, node: { ...edge.node, content } };
            }),
          },
        })) as ReportSectionsType[],
    );
  }, []);

  const reportType = reportFinal?.reportType as unknown as ReportTypeEnums;

  const isProgressSummaryGenerating = shouldPollForAiProgressSummary();
  const isNarrativeMappingRunning = shouldPollForNarrativeMapping();

  const [stepHeaderCompleteAction, setStepHeaderCompleteAction] = useState<StepHeaderCompleteAction | null>(null);

  useDismissGasPartialWarningOnLeave(reportFinal?.id);

  const isReportQueryLoading = reportQuery.isLoading;
  const contextValue = useMemo<ClinicalReportDetailsDataI>(
    () => ({
      reportType,
      isDiagnosisEvaluation: reportType === ReportTypeEnums.DiagnosisEvaluation,
      isInitialAssessment: reportType === ReportTypeEnums.InitialAssessment,
      isReassessment: reportType === ReportTypeEnums.Reassessment,
      reportSections: sections,
      previousAssessmentReports,
      assessmentFiles,
      downloadReportFileById,
      isReportCompleted,
      initialAssessmentTreatmentPlanQuery,
      appendContentToField,
      setFieldContent,
      isLoading: isReportQueryLoading,
      isReviewAndSignPage,
      isReportPage,
      activeTab,
      isProgrammingOrNarrativePage,
      isEHRScraperUser,
      isProgressSummaryGenerating,
      isNarrativeMappingInProgress: isNarrativeMappingRunning,
      isGasEnabled,
      isAddTargetsEnabled,
      stepHeaderCompleteAction,
      setStepHeaderCompleteAction,
    }),
    [
      reportType,
      sections,
      previousAssessmentReports,
      assessmentFiles,
      downloadReportFileById,
      isReportCompleted,
      initialAssessmentTreatmentPlanQuery,
      appendContentToField,
      setFieldContent,
      isReportQueryLoading,
      isReviewAndSignPage,
      isReportPage,
      activeTab,
      isProgrammingOrNarrativePage,
      isEHRScraperUser,
      isProgressSummaryGenerating,
      isNarrativeMappingRunning,
      isGasEnabled,
      isAddTargetsEnabled,
      stepHeaderCompleteAction,
    ],
  );

  // Early return AFTER all hooks have been called
  if (!reportQueryWithPolling || reportQueryWithPolling.isLoading) return null;

  return (
    <ClinicalReportDetailsContext.Provider value={contextValue}>
      <ReportProvider
        report={reportFinal ?? undefined}
        reportQuery={reportQueryWithPolling}
        viewMode={reportType === ReportTypeEnums.DiagnosisEvaluation && flags["db-single-page"] ? "single" : "step"}
      >
        {children}
      </ReportProvider>
    </ClinicalReportDetailsContext.Provider>
  );
};
